/**
 * Hook zwraca tylko logikę i dane - renderowanie jest w komponencie TableParameters
 */

import { useMemo } from 'react';
import { mapValues, keyBy } from 'lodash';

import { IWiseTableQueryParams, IActiveParameter, IWiseTableParameter } from 'components/wise/types';
import { useWiseTableReduxSync } from 'components/wise/WiseTable/hooks/useWiseTableReduxSync';

interface IUseWiseTableParametersParams {
  parameters: IWiseTableParameter[];
  queryParams: IWiseTableQueryParams;
  setQueryParams: React.Dispatch<React.SetStateAction<IWiseTableQueryParams>>;
  pageSymbol?: string;
  componentSymbol: string;
}

interface IUseWiseTableParametersReturn {
  parameters: IWiseTableParameter[];
  activeParameters: IActiveParameter[];
  handleClearParameters: () => void;
  updateParameter: (fieldSymbol: string, value: any) => void;
}

export const useWiseTableParameters = ({
  parameters,
  queryParams,
  setQueryParams,
  pageSymbol,
  componentSymbol
}: IUseWiseTableParametersParams): IUseWiseTableParametersReturn => {
  const { syncToRedux } = useWiseTableReduxSync({
    pageSymbol,
    componentSymbol
  });

  // Aktualizacja parametru z zapisem do Redux
  const updateParameter = (fieldSymbol: string, value: any): void => {
    setQueryParams((prevState) => {
      const newQueryParams = {
        ...prevState,
        [fieldSymbol]: value,
        page: 1
      };

      syncToRedux(newQueryParams);

      return newQueryParams;
    });
  };

  // Czyszczenie wszystkich parametrów
  const handleClearParameters = (): void => {
    const defaultParams: IWiseTableQueryParams = {
      page: 1,
      limit: queryParams.limit,
      ...(parameters
        ? mapValues(keyBy(parameters, 'field_symbol'), '')
        : {})
    };

    setQueryParams(defaultParams);

    syncToRedux(defaultParams);
  };

  // Generowanie listy aktywnych parametrów (dla tagów) - memoized
  const activeParameters: IActiveParameter[] = useMemo(
    () =>
      parameters
        .filter((param) => {
          const value = queryParams[param.field_symbol];
          return value !== undefined && value !== null && value !== '';
        })
        .map((param) => ({
          fieldSymbol: param.field_symbol,
          label: param.label,
          value: queryParams[param.field_symbol],
          displayValue: String(queryParams[param.field_symbol])
        })),
    [parameters, queryParams]
  );

  return {
    parameters,
    activeParameters,
    handleClearParameters,
    updateParameter
  };
};
